import { ReactNode } from 'react';
import { useIntl } from 'react-intl';

import CalmAppOnPhones from '@/../public/_n/images/b2b/Calm_App_On_Phones.png';
import ModeToggle from '@/components/authForm/ModeToggle';
import BackButton from '@/components/b2b/BackButton';
import Image from '@/components/image/NextImage';
import { CopyItem } from '@/components/valuePropList';
import { useAuthPrompt } from '@/context/auth/AuthPromptProvider';
import { useAnalytics } from '@/hooks/analytics';
import { useComponentDidMount } from '@/hooks/utils/useComponentDidMount';
import genericMessages from '@/messages/messages';
import { User } from '@/store/user/types';

import AuthFormWrapper from '../Components/AuthFormWrapper';
import { SignupScreenProps } from '../shared/types';
import AdditionalContent from './AdditionalContent';
import messages from './messages';
import {
	AccountContentWrapper,
	AccountValuePropsList,
	AuthColumnWrapper,
	B2BLoginForm,
	BackButtonContainer,
	CtaText,
	FaqLink,
	FaqText,
	ImageWrapper,
	LockIcon,
	MailIcon,
	PersonalEmail,
	ProfileIcon,
	TermsText,
	TwoColumnWrapper,
	ValuePropsListWrapper,
} from './styles';

const Account = ({
	onScreenChange,
	partnerDetails,
	onSuccess,
	isRevampRedempFlow,
}: Pick<SignupScreenProps, 'onScreenChange' | 'partnerDetails' | 'onSuccess' | 'isRevampRedempFlow'>) => {
	const { logEvent } = useAnalytics();
	const { formatMessage } = useIntl();
	const { authPrompt } = useAuthPrompt();
	const { faqLink, disableSocialSignup } = partnerDetails.pageInfo;
	const termsMessage = partnerDetails.requires_data_sharing
		? genericMessages.loginTermsDataSharing
		: genericMessages.signupTerms;

	useComponentDidMount(() => {
		logEvent({
			eventName: 'Account : Landed',
			eventProps: {
				partner_id: partnerDetails.id,
			},
		});
	});

	function onAuthSuccess(user: User): void {
		onSuccess(user);
	}

	const b2bLoginFormInputIcons = {
		name: ProfileIcon,
		email: MailIcon,
		password: LockIcon,
	};

	const accountValueProps: Array<CopyItem> = [
		{
			id: 'valueProp1',
			header: messages.valueProp1Head,
			body: messages.valueProp1Body,
		},
		{
			id: 'valueProp2',
			header: messages.valueProp2Head,
			body: messages.valueProp2Body,
		},
		{
			id: 'valueProp3',
			header: messages.valueProp3Head,
			body: messages.valueProp3Body,
		},
	];

	if (isRevampRedempFlow) {
		return (
			<>
				<TwoColumnWrapper>
					<AuthColumnWrapper>
						<AuthFormWrapper
							heading={authForm => {
								if (authForm === 'login') {
									return {
										message: messages.headingLogin,
									};
								}
								return {
									message: messages.headingSignup,
								};
							}}
							onAuthSuccess={onAuthSuccess}
							requiresDataSharing={Boolean(partnerDetails.requires_data_sharing)}
						/>
					</AuthColumnWrapper>
					<ValuePropsListWrapper>
						<ImageWrapper>
							<Image
								src={CalmAppOnPhones}
								alt={formatMessage(messages.recImage)}
								fill
								style={{ objectFit: 'contain' }}
							/>
						</ImageWrapper>
						<AccountValuePropsList
							isRevampRedempFlow
							isB2BAccountPage={true}
							copy={accountValueProps}
							title={formatMessage(messages.valuePropHeader, {
								br: <br></br>,
							})}
						/>
					</ValuePropsListWrapper>
				</TwoColumnWrapper>
				<AdditionalContent />
			</>
		);
	}

	return (
		<AccountContentWrapper centerAlign>
			<BackButtonContainer>
				<BackButton onScreenChange={onScreenChange} />
				<ModeToggle isB2B />
			</BackButtonContainer>
			{authPrompt === 'login' && <CtaText>{formatMessage(genericMessages.loginCalmTitle)}</CtaText>}
			{authPrompt === 'signup' && (
				<>
					<CtaText>{formatMessage(genericMessages.signupTitle2)}</CtaText>
					<PersonalEmail>{formatMessage(messages.continuedAccess)}</PersonalEmail>
				</>
			)}
			<B2BLoginForm
				className="b2bLoginForm"
				onAuthSuccess={onAuthSuccess}
				disableSocialSignup={disableSocialSignup}
				disableFormToggle
				inputIcons={b2bLoginFormInputIcons}
				showInputIcons
				isB2B
			/>

			<FaqText>
				{authPrompt === 'login' && (
					<>
						{formatMessage(messages.faq, {
							faqtext: (...chunks: ReactNode[]) => (
								<FaqLink href={faqLink} target="_blank">
									{chunks}
								</FaqLink>
							),
						})}
					</>
				)}
			</FaqText>
			<TermsText>
				{formatMessage(termsMessage, {
					terms: (
						<a href="/terms" target="_blank">
							{formatMessage(genericMessages.terms)}
						</a>
					),
					privacypolicylink: (...chunks) => (
						<a href="/privacy" target="_blank">
							{chunks}
						</a>
					),
				})}
			</TermsText>
		</AccountContentWrapper>
	);
};

export default Account;
